import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { GROUP_TYPES } from '../../utils/workflowStepGrouping';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const EMAIL_SOURCE_OPTIONS = [
  { value: '{{trigger.email}}', label: '{{trigger.email}}' },
  { value: '{{contact.email}}', label: '{{contact.email}}' },
  { value: 'custom', label: 'Custom merge field' },
];

const OUTPUT_FIELDS = [
  '{{reset_link}}',
  '{{reset_token}}',
  '{{reset_expires_at}}',
  '{{user.email}}',
  '{{user.first_name}}',
  '{{portal.reset_url}}',
];

const INTERNAL_STEPS = [
  'Find user',
  'Resolve portal',
  'Generate token',
  'Build reset link',
];

const defaultConfig = () => ({
  action_name: 'Prepare Password Reset',
  email_source: '{{trigger.email}}',
  custom_email_source: '',
  normalize_email: true,
  silent_if_not_found: true,
  prevent_token_reuse: true,
  require_active_portal_user: true,
  record_security_event: true,
});

const PreparePasswordResetEditor = ({
  isOpen,
  onClose,
  groupItem,
  onSave,
  advancedMode = false,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'prepare-password-reset',
  chromeHeight,
}) => {
  const [config, setConfig] = useState(defaultConfig());
  const [internalOpen, setInternalOpen] = useState(false);
  const [emailSourceMode, setEmailSourceMode] = useState('{{trigger.email}}');

  useEffect(() => {
    if (!isOpen || !groupItem) return;
    const incoming = { ...defaultConfig(), ...(groupItem.groupConfig || {}) };
    setConfig(incoming);
    const preset = EMAIL_SOURCE_OPTIONS.find((opt) => opt.value === incoming.email_source);
    setEmailSourceMode(preset ? incoming.email_source : 'custom');
  }, [isOpen, groupItem]);

  const resolvedEmailSource = useMemo(() => {
    if (emailSourceMode === 'custom') return config.custom_email_source || config.email_source;
    return emailSourceMode;
  }, [emailSourceMode, config.custom_email_source, config.email_source]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave?.({
      ...config,
      email_source: resolvedEmailSource,
    });
    onClose?.();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className="border-l border-slate-200 bg-white"
      testId="prepare-password-reset-editor"
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Prepare Password Reset</h2>
          <p className="text-xs text-slate-500">System preparation for the next Send Email step</p>
        </div>
        <button type="button" onClick={onClose} className="rounded p-2 text-slate-400 hover:bg-slate-100">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        <div>
          <Label htmlFor="ppr-action-name">Action Name</Label>
          <Input
            id="ppr-action-name"
            value={config.action_name}
            onChange={(e) => setConfig((prev) => ({ ...prev, action_name: e.target.value }))}
            className="mt-1"
          />
        </div>

        <div>
          <Label>Email Source</Label>
          <div className="mt-2 space-y-2">
            {EMAIL_SOURCE_OPTIONS.map((option) => (
              <label key={option.value} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="ppr-email-source"
                  checked={emailSourceMode === option.value}
                  onChange={() => setEmailSourceMode(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
          {emailSourceMode === 'custom' ? (
            <Input
              value={config.custom_email_source}
              onChange={(e) => setConfig((prev) => ({ ...prev, custom_email_source: e.target.value }))}
              placeholder="{{contact.custom_field}}"
              className="mt-2"
              data-testid="ppr-custom-email-source"
            />
          ) : null}
        </div>

        <div>
          <Label>Security Options</Label>
          <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
            {[
              ['normalize_email', 'Normalize email'],
              ['silent_if_not_found', 'Silent if user not found'],
              ['prevent_token_reuse', 'Prevent token reuse'],
              ['require_active_portal_user', 'Require active portal user'],
              ['record_security_event', 'Record security event'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(config[key])}
                  onChange={(e) => setConfig((prev) => ({ ...prev, [key]: e.target.checked }))}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <h3 className="text-sm font-semibold text-emerald-800">Output Variables</h3>
          <p className="mt-1 text-xs text-emerald-700">
            Use these outputs in the next Send Email, If / Else, or Wait action.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {OUTPUT_FIELDS.map((field) => (
              <code key={field} className="rounded bg-white px-2 py-1 text-xs text-emerald-700 border border-emerald-100">
                {field}
              </code>
            ))}
          </div>
        </div>

        {advancedMode ? (
          <div className="rounded-lg border border-slate-200">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-slate-700"
              onClick={() => setInternalOpen((open) => !open)}
              data-testid="ppr-internal-steps-toggle"
            >
              Internal steps
              {internalOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {internalOpen ? (
              <div className="border-t border-slate-100 px-4 py-3 text-sm text-slate-600" data-testid="ppr-internal-steps-preview">
                {INTERNAL_STEPS.join(' → ')}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800" data-testid="ppr-helper-message">
            This action securely prepares the reset link and merge fields for the next Send Email step.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSave} data-testid="ppr-save">
          Save action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default PreparePasswordResetEditor;
